/**
 * Centralized Privacy Service for Watchlists App
 * 
 * This service consolidates all privacy logic across:
 * - Profile visibility and permissions
 * - Watchlist access and sharing
 * - Review and rating visibility
 * - Social features (followers, following, likes)
 * 
 * Created to ensure consistent privacy enforcement and easy maintenance.
 */

import { User } from 'firebase/auth';
import { firebaseService } from './firebaseService';
import { Watchlist, PrivacyType } from '../types/watchlist';
import { UserProfile, unifiedGetDoc } from '@watchlist/shared';
import { authService } from './authService';
import { socialService } from './socialService';
import { getCachedUserProfile } from '../utils/profileCache';

// Short-lived cache: (viewerUid|targetUid) → { result, expiresAt }
const _viewerCache = new Map<string, { result: boolean; expiresAt: number }>();
const VIEWER_CACHE_TTL_MS = 30_000; // 30 seconds — one screen render lifecycle

// ===== PRIVACY TYPES AND INTERFACES =====

export type ProfileVisibility = 'public' | 'private' | 'friends';

export interface PrivacySettings {
  profile: {
    visibility: ProfileVisibility;
    showWatchlist: boolean;
    showRatings: boolean;
    showActivity: boolean;
  };
  watchlists: {
    defaultPrivacy: 'public' | 'private';
    allowCollaboration: boolean;
  };
  social: {
    discoverableInSearch: boolean;
    allowRecommendations: boolean;
  };
}

export interface PrivacyContext {
  currentUser?: User;
  targetUserId: string;
  viewerUserId?: string;
  isAuthenticated: boolean;
}

export interface PrivacyCheckResult {
  canView: boolean;
  canInteract: boolean;
  reason?: string;
  filteredData?: any;
}

// ===== PRIVACY SERVICE CLASS =====

class PrivacyService {
  
  // ===== CORE PRIVACY GATE =====

  /**
   * Central gate: can viewerUid see the social content of targetUid?
   * Returns true when:
   *   - viewer IS the target (own profile)
   *   - target profile is public
   *   - viewer is an approved follower of target
   *
   * Pass a pre-fetched targetProfile to avoid an extra Firestore read.
   */
  async canViewPrivateContent(
    viewerUid: string | undefined,
    targetUid: string,
    targetProfile?: UserProfile | null
  ): Promise<boolean> {
    // Own profile — always visible
    if (viewerUid && viewerUid === targetUid) return true;

    // Check cache first
    const cacheKey = `${viewerUid ?? 'anon'}|${targetUid}`;
    const cached = _viewerCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.result;

    try {
      const profile = targetProfile ?? await getCachedUserProfile(targetUid);

      let result: boolean;
      if (!profile || profile.privacy?.profileVisibility !== 'private') {
        // public (or missing profile — fail open so we don't break existing screens)
        result = true;
      } else if (!viewerUid) {
        // unauthenticated viewer on a private profile
        result = false;
      } else {
        // Private profile — check if viewer is an approved follower
        const followDoc = await unifiedGetDoc('userFollowing', `${viewerUid}_${targetUid}`);
        result = followDoc.exists();
      }

      _viewerCache.set(cacheKey, { result, expiresAt: Date.now() + VIEWER_CACHE_TTL_MS });
      return result;
    } catch (error) {
      console.error('PrivacyService: canViewPrivateContent error', error);
      return true; // fail open — never silently hide content due to a transient error
    }
  }

  /** Invalidate the viewer cache entry for a specific pair (call after follow/unfollow). */
  invalidateViewerCache(viewerUid: string, targetUid: string): void {
    _viewerCache.delete(`${viewerUid}|${targetUid}`);
  }

  /**
   * Three-layer content visibility check:
   *   1. Profile gate  (canViewPrivateContent)
   *   2. Settings toggle (showRatings / showWatchlist / showActivity)
   *   3. Per-item audience (reviewVisibility, shareToFeed, postAsStory)
   *
   * itemKind: 'review' | 'watchlist' | 'activity' | 'spotlight'
   * item: the document data (must include relevant audience fields)
   *
   * Owner always bypasses all layers.
   */
  async canViewContentItem(
    viewerUid: string | undefined,
    targetUid: string,
    targetProfile: UserProfile | null,
    itemKind: 'review' | 'watchlist' | 'activity' | 'spotlight',
    item: Record<string, any>
  ): Promise<boolean> {
    // Owner bypass
    if (viewerUid && viewerUid === targetUid) return true;

    // Layer 1: profile gate
    const profileAllowed = await this.canViewPrivateContent(viewerUid, targetUid, targetProfile);
    if (!profileAllowed) return false;

    // Layer 2: settings toggles on the target profile
    const privacy = targetProfile?.privacy;
    if (privacy) {
      if (itemKind === 'review' && privacy.showRatings === false) return false;
      if (itemKind === 'watchlist' && privacy.showWatchlist === false) return false;
      if (itemKind === 'activity' && privacy.showActivity === false) return false;
      if (itemKind === 'spotlight' && privacy.showActivity === false) return false;
    }

    // Layer 3: per-item audience
    if (itemKind === 'review' && item.reviewVisibility === 'only-me') return false;
    if (itemKind === 'activity' && item.shareToFeed === false) return false;
    if (itemKind === 'spotlight' && item.postAsStory === false) return false;

    return true;
  }

  // ===== PROFILE PRIVACY METHODS =====

  /**
   * Check if a user profile can be viewed by another user
   */
  async canViewProfile(
    targetUserId: string, 
    viewerUserId?: string
  ): Promise<PrivacyCheckResult> {
    try {
      const targetProfile = await getCachedUserProfile(targetUserId);
      
      if (!targetProfile) {
        return { canView: false, canInteract: false, reason: 'Profile not found' };
      }

      // Profile basic info is always visible (per requirements)
      // But detailed content depends on privacy settings
      return {
        canView: true,
        canInteract: targetProfile.privacy.profileVisibility === 'public',
        filteredData: await this.getFilteredProfileData(targetProfile, viewerUserId)
      };
    } catch (error) {
      console.error('Error checking profile view permissions:', error);
      return { canView: false, canInteract: false, reason: 'Error checking permissions' };
    }
  }

  /**
   * Get filtered profile data based on privacy settings
   */
  private async getFilteredProfileData(
    profile: UserProfile, 
    viewerUserId?: string
  ): Promise<Partial<UserProfile>> {
    const isOwnProfile = profile.uid === viewerUserId;
    const isPublicProfile = profile.privacy.profileVisibility === 'public';

    // Own profile: return everything
    if (isOwnProfile) {
      return profile;
    }

    // Base profile info (always visible per requirements)
    const filteredProfile: Partial<UserProfile> = {
      uid: profile.uid,
      displayName: profile.displayName,
      username: profile.username,
      photoURL: profile.photoURL,
      bio: profile.bio,
      location: profile.location,
      website: profile.website,
      favoriteGenres: profile.favoriteGenres,
      socialLinks: profile.socialLinks,
      stats: {
        ...profile.stats,
        // Email is never visible to others
      },
      // Privacy settings are not exposed to others
      privacy: undefined,
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt,
    };

    // For private profiles, show counts but not detailed content
    if (!isPublicProfile) {
      filteredProfile.stats = {
        moviesWatched: profile.stats.moviesWatched,
        reviewsWritten: profile.stats.reviewsWritten,
        listsCreated: profile.stats.listsCreated,
        followersCount: profile.stats.followersCount,
        followingCount: profile.stats.followingCount,
        likesReceived: profile.stats.likesReceived || 0,
      };
    }

    return filteredProfile;
  }

  // ===== SOCIAL PRIVACY METHODS =====

  /**
   * Get followers list with privacy filtering.
   * Visible to: own profile, public profiles, or approved followers of private profiles.
   */
  async getFollowersForProfile(
    targetUserId: string,
    viewerUserId?: string
  ): Promise<string[]> {
    try {
      const targetProfile = await getCachedUserProfile(targetUserId);
      // Bypass the viewer cache here — always do a fresh Firestore check so
      // an approved follower never gets an empty list from a stale cache entry.
      const canView = await this._canViewFresh(viewerUserId, targetUserId, targetProfile);
      if (!canView) return [];
      return await firebaseService.getFollowers(targetUserId);
    } catch (error) {
      console.error('Error getting filtered followers:', error);
      return [];
    }
  }

  /**
   * Get following list with privacy filtering.
   * Visible to: own profile, public profiles, or approved followers of private profiles.
   */
  async getFollowingForProfile(
    targetUserId: string,
    viewerUserId?: string
  ): Promise<string[]> {
    try {
      const targetProfile = await getCachedUserProfile(targetUserId);
      const canView = await this._canViewFresh(viewerUserId, targetUserId, targetProfile);
      if (!canView) return [];
      return await firebaseService.getFollowing(targetUserId);
    } catch (error) {
      console.error('Error getting filtered following:', error);
      return [];
    }
  }

  /** Like canViewPrivateContent but skips the short-lived viewer cache. */
  private async _canViewFresh(
    viewerUid: string | undefined,
    targetUid: string,
    targetProfile: any
  ): Promise<boolean> {
    if (viewerUid && viewerUid === targetUid) return true;
    if (!targetProfile || targetProfile.privacy?.profileVisibility !== 'private') return true;
    if (!viewerUid) return false;
    const followDoc = await unifiedGetDoc('userFollowing', `${viewerUid}_${targetUid}`);
    return followDoc.exists();
  }

  // ===== WATCHLIST PRIVACY METHODS =====

  /**
   * Check if a watchlist can be viewed by a user
   */
  async canViewWatchlist(
    watchlist: Watchlist, 
    viewerUserId?: string
  ): Promise<PrivacyCheckResult> {
    try {
      const isOwner = watchlist.createdBy === viewerUserId;
      const isCollaborator = watchlist.collaborators?.includes(viewerUserId || '') || false;
      const isPublic = !watchlist.isPrivate;

      // Owner and collaborators can always view
      if (isOwner || isCollaborator) {
        return { canView: true, canInteract: true };
      }

      // Public watchlists are viewable by everyone
      if (isPublic) {
        return { canView: true, canInteract: true };
      }

      // Private watchlists are not viewable by others
      return { canView: false, canInteract: false, reason: 'Watchlist is private' };
    } catch (error) {
      console.error('Error checking watchlist view permissions:', error);
      return { canView: false, canInteract: false, reason: 'Error checking permissions' };
    }
  }

  /**
   * Get privacy type for watchlist display
   */
  getWatchlistPrivacyType(watchlist: Watchlist): PrivacyType {
    const hasCollaborators = (watchlist.collaborators?.length || 0) > 0;
    
    if (watchlist.isPrivate && hasCollaborators) {
      return 'collaborative-private';
    } else if (!watchlist.isPrivate && hasCollaborators) {
      return 'collaborative-public';
    } else if (watchlist.isPrivate) {
      return 'private';
    } else {
      return 'public';
    }
  }

  /**
   * Get human-readable privacy description
   */
  getPrivacyDescription(type: PrivacyType, context: 'profile' | 'watchlist' = 'profile'): string {
    if (context === 'profile') {
      switch (type) {
        case 'public':
          return 'Anyone can see your reviews, spotlights, watchlists, crew posts, and activity.';
        case 'private':
          return 'Only approved followers can see your reviews, spotlights, watchlists, crew posts, and activity. Anyone can still find your profile by name or @handle.';
        default:
          return 'Privacy settings control what others can see.';
      }
    } else {
      switch (type) {
        case 'private':
          return 'Only you can see this list.';
        case 'public':
          return 'Anyone can see and like this list.';
        case 'collaborative-private':
          return 'Only you and collaborators can see this list.';
        case 'collaborative-public':
          return 'Anyone can see and like this collaborative list.';
        default:
          return 'Privacy settings control who can see this list.';
      }
    }
  }

  /**
   * Get default privacy settings for new users
   */
  getDefaultPrivacySettings(): PrivacySettings {
    return {
      profile: {
        visibility: 'public', // Per requirements: default to public
        showWatchlist: true,
        showRatings: true,
        showActivity: true,
      },
      watchlists: {
        defaultPrivacy: 'public', // Per requirements: default to public
        allowCollaboration: true,
      },
      social: {
        discoverableInSearch: true,
        allowRecommendations: true,
      },
    };
  }
}

// Export singleton instance
export const privacyService = new PrivacyService();
export default privacyService;

// Ensure this is treated as a module
export {};

// Ensure this file is treated as a module
export {};